import React, { useEffect, useState } from "react";
import {
    InviteTemplate,
    getInviteTemplate,
    getInviteTemplateDefaults,
    saveInviteTemplate,
} from "../../api/ProspectAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
    /** Called when the user leaves; saved tells the parent to reload. */
    onDone: (saved: boolean) => void;
}

/** One clearly-delineated settings section: number, title, accent color,
 *  what-it-does line — generous spacing so each part reads on its own. */
const Section: React.FC<{
    n: number;
    title: string;
    accent: string;
    description: React.ReactNode;
    children: React.ReactNode;
}> = ({ n, title, accent, description, children }) => (
    <div style={{
        border: "1px solid var(--border-color, #ddd)",
        borderLeft: `5px solid ${accent}`,
        borderRadius: "8px",
        padding: "1rem 1.25rem",
        marginBottom: "1.5rem",
        background: "var(--bg-secondary, #fafafa)",
    }}>
        <h3 style={{ margin: "0 0 0.2rem", fontSize: "1.05rem", textDecoration: "underline", textUnderlineOffset: "4px" }}>
            {n}. {title}
        </h3>
        <div className="item-description" style={{ marginBottom: "0.9rem" }}>{description}</div>
        {children}
    </div>
);

const Pre: React.FC<{ text: string }> = ({ text }) => (
    <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{text}</pre>
);

/** The server returns the EFFECTIVE text per field; the editor works on the
 *  override only. A field equal to its default means "no override" → blank. */
function toOverrides(effective: InviteTemplate, defs: InviteTemplate): InviteTemplate {
    const out = { ...effective };
    (Object.keys(out) as (keyof InviteTemplate)[]).forEach((k) => {
        out[k] = effective[k].trim() !== defs[k].trim() ? effective[k] : "";
    });
    return out;
}

/**
 * Full-page editor for everything an invite carries: sender, the two email
 * pairs, the text message, the {features} list size, and the why-page texts.
 * Each field shows the built-in default read-only, with an override box
 * below it — an empty box means the default is in use (and keeps tracking it
 * across deploys). Shown exclusively (the Outreach list is hidden while open).
 */
const InviteTemplateEditor: React.FC<Props> = ({ token, onDone }) => {
    // draft holds OVERRIDES only: "" = the field follows the default.
    const [draft, setDraft] = useState<InviteTemplate | null>(null);
    const [defaults, setDefaults] = useState<InviteTemplate | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([getInviteTemplate(token), getInviteTemplateDefaults(token)])
            .then(([effective, defs]) => {
                setDefaults(defs);
                setDraft(toOverrides(effective, defs));
            })
            .catch((e) => { logApiError(e); setError(getUserMessage(e)); });
    }, [token]);

    const save = async () => {
        if (!draft || !defaults) return;
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            // Blank fields tell the server "no override" — exactly our model.
            const effective = await saveInviteTemplate(token, draft);
            setDraft(toOverrides(effective, defaults));
            setInfo("Saved — new invites and why pages use it immediately.");
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const set = (patch: Partial<InviteTemplate>) => {
        setDraft((d) => (d ? { ...d, ...patch } : d));
        setInfo(null);
    };

    /** The text currently in effect for a field (override or default). */
    const effectiveOf = (k: keyof InviteTemplate): string =>
        draft && draft[k].trim() !== "" ? draft[k] : (defaults ? defaults[k] : "");

    /** Default (read-only) + override box for one field. */
    const field = (label: string, k: keyof InviteTemplate, rows?: number) => {
        if (!draft || !defaults) return null;
        const customized = draft[k].trim() !== "";
        return (
            <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label style={{ fontWeight: 700 }}>
                    {label}
                    {customized ? (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", fontWeight: 400, color: "#b45309" }}>
                            customized ✓
                            <button
                                type="button"
                                className="btn-sm btn-secondary"
                                style={{ marginLeft: "0.4rem" }}
                                onClick={() => set({ [k]: "" } as Partial<InviteTemplate>)}
                                disabled={busy}
                                title="Clear the override — the field follows the default again"
                            >
                                Reset to default
                            </button>
                        </span>
                    ) : (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", fontWeight: 400, color: "var(--text-secondary)" }}>
                            using the default
                        </span>
                    )}
                </label>
                <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", margin: "0.15rem 0" }}>
                    Default (read-only):
                </div>
                <div style={{
                    border: "1px dashed var(--border-color, #bbb)", borderRadius: "4px",
                    padding: "0.4rem 0.6rem", background: "var(--bg-primary, #fff)",
                    fontSize: "0.85rem", color: "var(--text-secondary)",
                    maxHeight: "200px", overflowY: "auto",
                }}>
                    <Pre text={defaults[k]} />
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", margin: "0.35rem 0 0.15rem" }}>
                    Override (leave empty to use the default):
                </div>
                {rows ? (
                    <textarea
                        rows={rows}
                        value={draft[k]}
                        placeholder="— using the default above — type here to override"
                        onChange={(e) => set({ [k]: e.target.value } as Partial<InviteTemplate>)}
                        disabled={busy}
                    />
                ) : (
                    <input
                        value={draft[k]}
                        placeholder="— using the default above — type here to override"
                        onChange={(e) => set({ [k]: e.target.value } as Partial<InviteTemplate>)}
                        disabled={busy}
                    />
                )}
            </div>
        );
    };

    if (!draft || !defaults) {
        return (
            <div>
                <button className="btn-secondary" onClick={() => onDone(false)}>← Back to Outreach</button>
                {error
                    ? <div className="error-message" style={{ marginTop: "0.75rem" }}>{error}</div>
                    : <p style={{ color: "var(--text-secondary)", marginTop: "0.75rem" }}>Loading template…</p>}
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                <button className="btn-secondary" onClick={() => onDone(false)} disabled={busy}>
                    ← Back to Outreach
                </button>
                <h2 style={{ margin: 0 }}>Invite template</h2>
            </div>
            <div className="item-description" style={{ marginBottom: "1.5rem", maxWidth: "720px" }}>
                Defaults ship with the code (seeded from outreach_seed.sql on every
                deploy); an override is stored in the database and wins until you
                reset it. Placeholders filled per prospect: <strong>{"{name}"}</strong> →
                their name (or org, or “there”); <strong>{"{company}"}</strong> → the
                organization; <strong>{"{category}"}</strong> → their category;{" "}
                <strong>{"{features}"}</strong> → the category's parameter bullets;{" "}
                <strong>{"{why_link}"}</strong> → their personal comparison page;{" "}
                <strong>{"{post_link}"}</strong> → their current post's URL;{" "}
                <strong>{"{claim_link}"}</strong> → the take-ownership page of their
                managed org (lines containing either are dropped for prospects
                without one);{" "}
                <strong>{"{key_details}"}</strong> (why page) → the category's parameter
                names. Write links with https:// — babahanks.com links are
                click-tracked automatically. To show a link as words instead of
                the URL, write <strong>[the words](https://link)</strong> — emails
                render it as a clickable label; texts send it as "the words: link".
            </div>

            <Section n={1} title="Sender" accent="#64748b"
                     description="From-address on invite emails. Replies land here, so it must exist as a mailbox or alias (e.g. in Fastmail).">
                {field("From address (used by all emails)", "fromEmail")}
            </Section>

            <Section n={2} title="Email — regular categories (products)" accent="#6b7280"
                     description="Sent when the prospect's category is a product category.">
                {field("Subject", "subject")}
                {field("Message", "message", 10)}
            </Section>

            <Section n={3} title="Email — service categories" accent="var(--primary-color, #4f46e5)"
                     description={<>Sent when the prospect's category is a service (the “service” tag in the prospect list).</>}>
                {field("Subject", "serviceSubject")}
                {field("Message", "serviceMessage", 10)}
            </Section>

            <Section n={4} title="Text / WhatsApp message" accent="#0e7490"
                     description="Prefilled for texts and WhatsApp. Watch the compose box's segment counter — carriers refuse very long multi-part SMS.">
                {field("Message", "smsMessage", 10)}
            </Section>

            <Section n={5} title="{features} list size" accent="#b45309"
                     description="How many options of each choice parameter the {features} bullet list shows (e.g. Services Offered lists this many, then “and more”).">
                {field("Choices shown per parameter", "featureChoiceCount")}
            </Section>

            <Section n={6} title="Why page" accent="#7c3aed"
                     description={<>The public comparison page prospects open from {"{why_link}"} —
                         the form-first design. Plain text everywhere; the pitch's first
                         sentence is shown bold on the page.</>}>
                {field("Pitch (top of the page, above the try-it form)", "whyPitch", 4)}
                {field("Form nudge (above the parameter dropdowns)", "whyFormNudge", 3)}
                {field("Find-you-by-place — service categories", "whyPlaceService", 3)}
                {field("Find-you-by-place — location-based categories", "whyPlaceLocation", 3)}
                {field("Closing — searching uses the same form", "whySameForm", 4)}
            </Section>

            <Section n={7} title="Claiming" accent="#16a34a"
                     description={<>The claim-your-listing email (sent when the owner asks for the
                         ownership link — placeholders {"{company}"} and {"{claim_link}"}), and the
                         shared-inbox list: emails whose part before the @ is on this
                         comma-separated list never get one-click claim links — their listings
                         show the "leave your email, we verify" form instead.</>}>
                {field("Claim email — subject", "claimSubject")}
                {field("Claim email — message", "claimMessage", 8)}
                {field("Shared-inbox local parts (comma-separated)", "genericEmailLocals", 2)}
            </Section>

            {/* One save for the whole page, pinned while scrolling. */}
            <div style={{ position: "sticky", bottom: 0, background: "var(--bg-primary, #fff)", borderTop: "2px solid var(--border-color, #ddd)", padding: "0.75rem 0", zIndex: 5 }}>
                <button onClick={save} disabled={busy}>
                    {busy ? "Saving…" : "Save template"}
                </button>
                <button className="btn-secondary" onClick={() => onDone(true)} disabled={busy} style={{ marginLeft: "0.5rem" }}>
                    Done
                </button>
                <span style={{ marginLeft: "0.75rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    Saves all sections. Empty override boxes keep following the defaults.
                </span>
                {error && <div className="error-message" style={{ marginTop: "0.5rem" }}>{error}</div>}
                {info && <div className="success-message" style={{ marginTop: "0.5rem" }}>{info}</div>}
            </div>
        </div>
    );
};

export default InviteTemplateEditor;